import type { VoucherGetByCodeResponse } from '@diamond/shared/types';
import { useQuery } from '@tanstack/react-query';
import shallow from 'zustand/shallow';

import { getVoucherDetailByCode } from '../queries';
import { useAuthStore, useShipToStore } from '../stores';

export const VOUCHER_DETAIL_KEY = 'VoucherDetail';

/**
 * Custom hook to fetch voucher details by code for display purposes.
 * Uses TanStack Query for caching and automatic refetching.
 *
 * @param voucherCode - The voucher code to fetch details for
 * @returns TanStack Query result with voucher data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useVoucherDetail('SAVE20');
 * ```
 */
export const useVoucherDetail = (voucherCode?: string) => {
  const session = useAuthStore();
  const warehouse = useShipToStore((s) => s.selectedShipToId, shallow);

  return useQuery({
    queryKey: [VOUCHER_DETAIL_KEY, voucherCode, warehouse],
    queryFn: () =>
      getVoucherDetailByCode(session.access_token, voucherCode!, warehouse),
    enabled: !!voucherCode && !!session.access_token && !!warehouse,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};
